import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { enableProdMode } from '@angular/core';
import * as Sentry from "@sentry/angular";

if (environment.production) {
  enableProdMode();
}

// Frames reported against the page URL itself (no file extension) come from script the in-app browser
// injects, such as the Google app or Chrome on iOS. index.html has no inline script of ours, so if one is
// ever added, its errors are dropped too.
const pageUrl = new RegExp(`^${window.location.origin.replace(/\./g, '\\.')}/[^.?#]*([?#].*)?$`);

Sentry.init({
  dsn: environment.sentryTokenUrl,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  ignoreErrors: ['window.ethereum.selectedAddress', 'variable: __firefox__', 'window.__firefox__.refresh_youtube', 'Load failed (signalr-svc-prod.saraplus.com)'],
  integrations: [
    Sentry.replayIntegration()
  ],
  denyUrls: [
    /^safari-extension:\/\//, // Ignore errors from Safari extensions
    /^safari-web-extension:\/\//, // Ignore errors from Safari web extensions
    /^chrome-extension:\/\//, // Ignore errors from Chrome extensions
    /^moz-extension:\/\//, // Ignore errors from Firefox extensions
    pageUrl // Ignore errors from scripts injected into the page
  ],
  // Session Replay
  replaysSessionSampleRate: environment.production ? 1.0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment : environment.sourceName,

  beforeSend(event, hint) {
    const error: any = hint?.originalException;
    const message = error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
    const stack = error instanceof Error ? error.stack ?? '' : '';

    // suppress primeNG-17 specific android error which is not actionable
    if (
      message.includes("reading 'begin'") &&
      (stack.includes('primeng') || stack.includes('handleAndroidInput'))
    ) {
      return null;
    }

    if (
      error?.name === 'RangeError' &&
      message.includes('Maximum call stack size exceeded') &&
      !/https?:\/\//.test(stack)
    ) {
      return null;
    }


    // Suppress network / offline errors
    if (
      error?.status === 0 ||
      message?.includes('not connected to the internet') ||
      message?.includes('HTTP 0') ||
      message?.includes('WebSocket closed with status code: 1006') ||
      message?.includes('The underlying connection was closed before the hub handshake could complete') ||
      message?.includes("Client hasn't sent a message/ping within the configured ClientTimeoutInterval") ||
      // SignalR drops on locked phones / frozen tabs; the hub reconnects or restarts and re-reads getcurrent (11610)
      message?.includes('Server timeout elapsed without receiving a message from the server') ||
      message?.includes('WebSocket is not in the OPEN state') ||
      message?.includes("Failed to complete negotiation with the server: TypeError: Load failed") ||
      message?.includes('Load failed') ||
      event.exception?.values?.some(v =>
        v.value?.includes('not connected to the internet')
      )
    ) {
      return null; // Drop event
    }

    return event; // Allow everything else
  }
});

// assets/browser-check.js sets this and shows an update message when the browser is older
// than the build supports. Don't start the app there: getcurrent would consume the link.
if ((window as { __sdUnsupported?: boolean }).__sdUnsupported) {
  Sentry.setTag('UniqueId', new URLSearchParams(window.location.search).get('id') ?? 'unknown');
  Sentry.captureMessage('Unsupported browser: app not started', 'warning');
} else {
  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(err => console.error(err));
}
